/**
 * rb-print.js — full-design PDF via the browser's print engine.
 *
 * Writes the shared RbRender output (target:'print') into an isolated, hidden
 * same-origin iframe with a dedicated print stylesheet and the locale's Noto
 * @font-face, then calls print(). The user picks "Save as PDF" — the result
 * keeps EVERY template design (2-column sidebars, RTL Arabic, colour bands),
 * the text stays selectable (ATS-readable), the browser embeds + subsets the
 * fonts, and multi-page pagination is automatic.
 *
 * Depends on: RbRender, RbFonts.
 * globalThis.RbPrint
 */
(function () {
    'use strict';

    function wrapDoc(bodyHtml, opts) {
        var locale = opts.locale || globalThis.RbI18n.LOCALE;
        var rtl = !!opts.rtl;
        var pg = globalThis.RbState.pageSize(opts.pageSize);
        var css =
            globalThis.RbFonts.printFontFaceCss(locale) +
            '*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
            'html,body{margin:0;padding:0;background:#fff}' +
            /* Pages after the first need their own top and bottom margin — with
               margin:0 a second page started flush at the paper edge (audit
               2026-10-09). The first page keeps the document's own padding on top.
               The page's min-height must shrink by the same 12 mm, or a full single
               page spills a blank second one. Keep 12 mm in sync with rb-preview.js. */
            '@page{size:' + pg.css + ';margin:12mm 0}@page:first{margin-top:0}' +
            '.rb-doc{margin:0 auto!important;transform:none!important;box-shadow:none!important;min-height:0!important;position:static!important}' +
            '.rb-doc[data-rb-layout^=sidebar]>div{min-height:calc(' + pg.h + 'px - 12mm - 2px)!important}' +
            'header{break-inside:avoid}' +
            'h1,h2{break-after:avoid}' +
            'ul,p{orphans:2;widows:2}' +
            '@media print{.rb-doc{width:' + pg.w + 'px}}';
        return '<!doctype html><html lang="' + locale + '" dir="' + (rtl ? 'rtl' : 'ltr') + '">' +
            '<head><meta charset="utf-8"><title>' + (opts.title ? esc(opts.title) : 'Resume') +
            '</title><style>' + css + '</style></head><body>' + bodyHtml + '</body></html>';
    }

    function esc(s) {
        return String(s == null ? '' : s).replace(/[<>&]/g, function (c) {
            return c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;';
        });
    }

    function buildDoc(model, opts) {
        var body = globalThis.RbRender.render(model, {
            target: 'print', dateFormat: opts.dateFormat, rtl: !!opts.rtl
        });
        return wrapDoc(body, {
            locale: opts.locale, rtl: opts.rtl,
            pageSize: model.meta && model.meta.pageSize,
            title: (model.personal && model.personal.name) || 'Resume'
        });
    }

    /* Wait for fonts + images inside the iframe document to settle. */
    function waitReady(win, doc) {
        var jobs = [];
        if (doc.fonts && doc.fonts.ready) {
            jobs.push(Promise.race([
                doc.fonts.ready,
                new Promise(function (r) { setTimeout(r, 2500); })
            ]));
        }
        var imgs = Array.prototype.slice.call(doc.images || []);
        imgs.forEach(function (img) {
            if (img.complete) return;
            jobs.push(new Promise(function (r) {
                img.addEventListener('load', r);
                img.addEventListener('error', r);
                setTimeout(r, 2500);
            }));
        });
        return Promise.all(jobs);
    }

    /* Core: print a complete HTML document string. Resolves once print() has
       been invoked (not when the dialog closes). */
    function printDocument(fullHtml) {
        return new Promise(function (resolve, reject) {
            var iframe = document.createElement('iframe');
            iframe.setAttribute('aria-hidden', 'true');
            iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:820px;height:1200px;border:0;visibility:hidden';
            document.body.appendChild(iframe);

            var cleaned = false;
            function cleanup() {
                if (cleaned) return;
                cleaned = true;
                setTimeout(function () {
                    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
                }, 300);
            }

            iframe.onload = function () {
                var win = iframe.contentWindow;
                var doc = iframe.contentDocument;
                if (!win || !doc) { cleanup(); reject(new Error('print iframe unavailable')); return; }
                waitReady(win, doc).then(function () {
                    win.addEventListener('afterprint', cleanup);
                    try {
                        win.focus();
                        win.print();
                        resolve();
                    } catch (e) {
                        cleanup();
                        reject(e);
                        return;
                    }
                    /* fallback cleanup if afterprint never fires */
                    setTimeout(cleanup, 60000);
                });
            };

            try {
                iframe.srcdoc = fullHtml;
            } catch (e) {
                /* older engines without srcdoc */
                var d = iframe.contentDocument;
                d.open(); d.write(fullHtml); d.close();
            }
        });
    }

    /* Print the résumé (full-design PDF). */
    function printResume(model, opts) {
        opts = opts || {};
        return printDocument(buildDoc(model, opts));
    }

    /* Print an arbitrary A4 body (used by the cover letter). bodyHtml should
       contain a 794px-wide .rb-doc element. */
    function printHtml(bodyHtml, opts) {
        opts = opts || {};
        return printDocument(wrapDoc(bodyHtml, opts));
    }

    globalThis.RbPrint = {
        printResume: printResume,
        printHtml: printHtml,
        buildDoc: buildDoc,
        wrapDoc: wrapDoc
    };
})();
